import { MentionClass, RelationshipDirection, RelationshipType } from '../interfaces/mention'

/**
 * Controlled vocabulary for `mentions` relationship edges, with each type's class,
 * directionality, via-eligibility, and its FUTURE hard-CRM mapping.
 *
 * This is the single source of truth for `class` / `direction` / `viaEligible`
 * (they are derived from the type, never stored as row columns, so a row can
 * never contradict its own type). `hardMap` is REFERENCE DATA ONLY in this
 * iteration — nothing promotes yet. It documents, per type, which CRM table a
 * future promotion iteration would target so a detected fact maps cleanly onto
 * an existing record.
 */

/** CRM tables a soft edge could eventually promote into. */
export type HardRelationshipTable = 'referral' | 'investment' | 'person_org_company_relationship'

/** How a type would map onto a hard CRM record (future promotion iteration). */
export interface HardRelationshipTarget {
  table: HardRelationshipTable
  /** Fixed value written into the table's free-text kind field (`role`/`type`), if any. */
  roleOrType?: string
  /**
   * When true, promotion additionally requires a party to be a Dwight-tracked
   * entity (prospect/client/opportunity). e.g. a16z→Jonathan (`introduced`,
   * Dwight not a party) fails this gate and stays soft/graph-only.
   */
  dwightPartyGated?: boolean
  note?: string
}

export interface RelationshipTypeMeta {
  class: MentionClass
  direction: RelationshipDirection
  /** Whether a facilitator (`via`) role is meaningful for this type. */
  viaEligible: boolean
  /** Future hard-CRM promotion target, or null for soft/graph-only types. */
  hardMap: HardRelationshipTarget | null
}

const rel = MentionClass.Relationship
const com = MentionClass.Commercial
const inc = MentionClass.Incidental

export const RELATIONSHIP_TYPE_CATALOG: Record<RelationshipType, RelationshipTypeMeta> = {
  [RelationshipType.Introduced]: {
    class: rel,
    direction: 'symmetric',
    viaEligible: true,
    hardMap: {
      table: 'referral',
      dwightPartyGated: true,
      note: 'Promotes to a referral only when a party is Dwight-tracked; third-party intros (a16z→Jonathan) stay soft/graph-only.',
    },
  },
  [RelationshipType.ReferredBy]: {
    class: rel,
    direction: 'directed',
    viaEligible: true,
    hardMap: { table: 'referral', dwightPartyGated: true },
  },
  [RelationshipType.MentionedBy]: {
    class: rel,
    direction: 'directed',
    viaEligible: false,
    hardMap: null,
  },
  [RelationshipType.InvestedIn]: {
    class: rel,
    direction: 'directed',
    viaEligible: false,
    hardMap: { table: 'investment' },
  },
  [RelationshipType.AdvisorTo]: {
    class: rel,
    direction: 'directed',
    viaEligible: false,
    hardMap: { table: 'person_org_company_relationship', roleOrType: 'advisor' },
  },
  [RelationshipType.BoardMember]: {
    class: rel,
    direction: 'directed',
    viaEligible: false,
    hardMap: { table: 'person_org_company_relationship', roleOrType: 'board_member' },
  },
  [RelationshipType.WorksAt]: {
    class: rel,
    direction: 'directed',
    viaEligible: false,
    hardMap: { table: 'person_org_company_relationship', roleOrType: 'employee' },
  },
  [RelationshipType.FormerEmployer]: {
    class: rel,
    direction: 'directed',
    viaEligible: false,
    hardMap: { table: 'person_org_company_relationship', roleOrType: 'former_employee' },
  },
  [RelationshipType.Knows]: {
    class: rel,
    direction: 'symmetric',
    viaEligible: false,
    hardMap: null,
  },
  [RelationshipType.Colleague]: {
    class: rel,
    direction: 'symmetric',
    viaEligible: false,
    hardMap: {
      table: 'person_org_company_relationship',
      roleOrType: 'colleague',
      note: 'Only promotable when the shared organization is known.',
    },
  },
  [RelationshipType.WorksWith]: {
    class: rel,
    direction: 'symmetric',
    viaEligible: false,
    hardMap: null,
  },
  [RelationshipType.Retailer]: {
    class: com,
    direction: 'directed',
    viaEligible: false,
    hardMap: null,
  },
  [RelationshipType.SalesChannel]: {
    class: com,
    direction: 'directed',
    viaEligible: false,
    hardMap: null,
  },
  [RelationshipType.Customer]: {
    class: com,
    direction: 'directed',
    viaEligible: false,
    hardMap: null,
  },
  [RelationshipType.Distributor]: {
    class: com,
    direction: 'directed',
    viaEligible: false,
    hardMap: null,
  },
  [RelationshipType.Supplier]: {
    class: com,
    direction: 'directed',
    viaEligible: false,
    hardMap: null,
  },
  [RelationshipType.Comparison]: {
    class: inc,
    direction: 'symmetric',
    viaEligible: false,
    hardMap: null,
  },
  [RelationshipType.Other]: {
    class: inc,
    direction: 'symmetric',
    viaEligible: false,
    hardMap: null,
  },
}

const VALID_RELATIONSHIP_TYPES = new Set<string>(Object.values(RelationshipType))

/** Coerce a raw model string to a known RelationshipType, defaulting to `Other`. */
export function coerceRelationshipType(raw: unknown): RelationshipType {
  return typeof raw === 'string' && VALID_RELATIONSHIP_TYPES.has(raw)
    ? (raw as RelationshipType)
    : RelationshipType.Other
}

/** Catalog metadata for a type. */
export function relationshipTypeMeta(type: RelationshipType): RelationshipTypeMeta {
  return RELATIONSHIP_TYPE_CATALOG[type]
}

/** Canonical class for a type (derived; never trust the model's own class). */
export const relationshipClass = (type: RelationshipType): MentionClass =>
  RELATIONSHIP_TYPE_CATALOG[type].class

/** Whether the `from`/`to` order carries meaning for this type. */
export const isDirected = (type: RelationshipType): boolean =>
  RELATIONSHIP_TYPE_CATALOG[type].direction === 'directed'
